/**
 * B106 — the admin shell chrome: left sidebar (brand + the five
 * destinations + utility links), top header (search/⌘K slot, actions
 * slot), full-width content. Chrome ONLY per the B78 kit stance: the
 * surface owns routing and behavior; every destination renders as a REAL
 * anchor with a stable href so navigation is a link, not a listener —
 * keyboard-focusable, middle-clickable, back-button honest (the B110(a)
 * dead-click class walled by construction). The brand components are
 * transcriptions of packages/ui/src/brand/*-v2.svg (B354); the shell and
 * brand tests wall them against the files' own path data so they cannot
 * drift, and wall the SVGs themselves to a single tone.
 */
import type { ReactNode } from "react";
import { NewTabHint } from "./components.js";
import { useViewFocus } from "./viewFocus.js";


/**
 * B352 — THE one app canvas (DESIGN-V2 §2): the token gradient plus two
 * fixed whisper glows, painted by tokens.css. Every full-page surface
 * composes this instead of painting its own background — the app-drift
 * wall refuses gradients/glows/frost in apps, so screen-to-screen canvas
 * drift is impossible by construction, not by review. Chrome only: no
 * listeners, no state; theming rides the same [data-transact-theme]
 * attribute as every other token.
 */
export function AppCanvas(props: { readonly className?: string; readonly children?: ReactNode }): ReactNode {
  return <div className={props.className ? `t-app-canvas ${props.className}` : "t-app-canvas"}>{props.children}</div>;
}

/**
 * B354 — the v2 brand (DESIGN-V2 §6). Three transcriptions, one tone:
 * the lowercase t + four-point spark ("V3"), always currentColor.
 * BrandMark = the bare glyph (inline-on-glass contexts); BrandTile = the
 * glyph on its fixed rounded tile (radius 24% of side, `--transact-tile`,
 * same in both themes — the glyph ink is pinned by the sheet, not by the
 * surface); BrandWordmark = "transact" as the approved flattened Poppins
 * outlines, never live text.
 */

/** The v2 mark glyph from brand/logo-mark-v2.svg — currentColor, no tile. */
export function BrandMark(props: { readonly className?: string; readonly size?: number }): ReactNode {
  const size = props.size ?? 24;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 72 72"
      fill="none"
      role="img"
      aria-label="Transact"
      className={props.className ? `t-brand-mark ${props.className}` : "t-brand-mark"}
      width={size}
      height={size}
    >
      <title>Transact</title>
      <path
        d="M14.8966 13.6552V40.9655C14.8966 49.6552 20.4828 53.3793 29.1724 52.1379"
        stroke="currentColor"
        strokeWidth="8.06897"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M13.6552 27.3103H24.8276" stroke="currentColor" strokeWidth="8.06897" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M41.5862 16.1379C42.5545 20.9793 44.8138 23.2386 49.6552 24.2069C44.8138 25.1752 42.5545 27.4345 41.5862 32.2759C40.6179 27.4345 38.3586 25.1752 33.5172 24.2069C38.3586 23.2386 40.6179 20.9793 41.5862 16.1379Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** The v2 tile from brand/logo-tile-v2.svg — fixed `--transact-tile` face, glyph ink from the sheet. */
export function BrandTile(props: { readonly className?: string; readonly size?: number }): ReactNode {
  const size = props.size ?? 32;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 72 72"
      fill="none"
      role="img"
      aria-label="Transact"
      className={props.className ? `t-brand-tile ${props.className}` : "t-brand-tile"}
      width={size}
      height={size}
    >
      <title>Transact</title>
      <rect width="72" height="72" rx="17.28" fill="var(--transact-tile, #39455F)" />
      <path
        d="M14.8966 13.6552V40.9655C14.8966 49.6552 20.4828 53.3793 29.1724 52.1379"
        stroke="currentColor"
        strokeWidth="8.06897"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M13.6552 27.3103H24.8276" stroke="currentColor" strokeWidth="8.06897" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M41.5862 16.1379C42.5545 20.9793 44.8138 23.2386 49.6552 24.2069C44.8138 25.1752 42.5545 27.4345 41.5862 32.2759C40.6179 27.4345 38.3586 25.1752 33.5172 24.2069C38.3586 23.2386 40.6179 20.9793 41.5862 16.1379Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * The v2 wordmark from brand/logo-wordmark-v2.svg — "transact" as the
 * approved flattened Poppins outlines (B357's export), currentColor. Not
 * live text: the vector IS the approved shape at any scale, with no font
 * loading race on tenant pages.
 */
export function BrandWordmark(props: { readonly className?: string; readonly height?: number }): ReactNode {
  const height = props.height ?? 18;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 198 33"
      fill="none"
      role="img"
      aria-label="Transact"
      className={props.className ? `t-brand-wordmark ${props.className}` : "t-brand-wordmark"}
      height={height}
    >
      <title>Transact</title>
      <path
        d="M9.522 11.592V23.92C9.522 24.7787 9.72133 25.4073 10.12 25.806C10.5493 26.174 11.2547 26.358 12.236 26.358H15.226V31.786H11.178C5.75 31.786 3.036 29.1487 3.036 23.874V11.592H0V6.302H3.036V0H9.522V6.302H15.226V11.592H9.522Z"
        fill="currentColor"
      />
      <path
        d="M26.298 10.258C27.126 8.90867 28.1993 7.85067 29.518 7.084C30.8673 6.31733 32.4007 5.934 34.118 5.934V12.696H32.416C30.392 12.696 28.8587 13.1713 27.816 14.122C26.804 15.0727 26.298 16.7287 26.298 19.09V31.786H19.858V6.302H26.298V10.258Z"
        fill="currentColor"
      />
      <path
        d="M36.7996 18.952C36.7996 16.376 37.3056 14.0913 38.3176 12.098C39.3603 10.1047 40.7556 8.57133 42.5036 7.498C44.2823 6.42467 46.2603 5.888 48.4376 5.888C50.339 5.888 51.995 6.27133 53.4056 7.038C54.847 7.80467 55.997 8.77067 56.8556 9.936V6.302H63.3416V31.786H56.8556V28.06C56.0276 29.256 54.8776 30.2527 53.4056 31.05C51.9643 31.8167 50.293 32.2 48.3916 32.2C46.245 32.2 44.2823 31.648 42.5036 30.544C40.7556 29.44 39.3603 27.8913 38.3176 25.898C37.3056 23.874 36.7996 21.5587 36.7996 18.952ZM56.8556 19.044C56.8556 17.48 56.549 16.146 55.9356 15.042C55.3223 13.9073 54.4943 13.0487 53.4516 12.466C52.409 11.8527 51.2896 11.546 50.0936 11.546C48.8976 11.546 47.7936 11.8373 46.7816 12.42C45.7696 13.0027 44.9416 13.8613 44.2976 14.996C43.6843 16.1 43.3776 17.4187 43.3776 18.952C43.3776 20.4853 43.6843 21.8347 44.2976 23C44.9416 24.1347 45.7696 25.0087 46.7816 25.622C47.8243 26.2353 48.9283 26.542 50.0936 26.542C51.2896 26.542 52.409 26.2507 53.4516 25.668C54.4943 25.0547 55.3223 24.196 55.9356 23.092C56.549 21.9573 56.8556 20.608 56.8556 19.044Z"
        fill="currentColor"
      />
      <path
        d="M83.7534 5.934C86.7894 5.934 89.2428 6.9 91.1134 8.832C92.9841 10.7333 93.9194 13.4013 93.9194 16.836V31.786H87.4794V17.71C87.4794 15.686 86.9734 14.1373 85.9614 13.064C84.9494 11.96 83.5694 11.408 81.8214 11.408C80.0428 11.408 78.6321 11.96 77.5894 13.064C76.5774 14.1373 76.0714 15.686 76.0714 17.71V31.786H69.6314V6.302H76.0714V9.476C76.9301 8.372 78.0188 7.51333 79.3374 6.9C80.6868 6.256 82.1588 5.934 83.7534 5.934Z"
        fill="currentColor"
      />
      <path
        d="M109.796 32.2C107.71 32.2 105.84 31.832 104.184 31.096C102.528 30.3293 101.209 29.302 100.228 28.014C99.2769 26.726 98.7555 25.3 98.6635 23.736H105.15C105.272 24.7173 105.748 25.53 106.576 26.174C107.434 26.818 108.492 27.14 109.75 27.14C110.976 27.14 111.927 26.8947 112.602 26.404C113.307 25.9133 113.66 25.2847 113.66 24.518C113.66 23.69 113.23 23.0767 112.372 22.678C111.544 22.2487 110.21 21.7887 108.37 21.298C106.468 20.838 104.904 20.3627 103.678 19.872C102.482 19.3813 101.439 18.63 100.55 17.618C99.6909 16.606 99.2615 15.2413 99.2615 13.524C99.2615 12.1133 99.6602 10.8253 100.458 9.66C101.286 8.49467 102.451 7.57467 103.954 6.9C105.487 6.22533 107.281 5.888 109.336 5.888C112.372 5.888 114.794 6.65467 116.604 8.188C118.413 9.69067 119.41 11.73 119.594 14.306H113.43C113.338 13.294 112.908 12.4967 112.142 11.914C111.406 11.3007 110.409 10.994 109.152 10.994C107.986 10.994 107.082 11.2087 106.438 11.638C105.824 12.0673 105.518 12.6653 105.518 13.432C105.518 14.2907 105.947 14.95 106.806 15.41C107.664 15.8393 108.998 16.284 110.808 16.744C112.648 17.204 114.166 17.6793 115.362 18.17C116.558 18.6607 117.585 19.4273 118.444 20.47C119.333 21.482 119.793 22.8313 119.824 24.518C119.824 25.99 119.41 27.3087 118.582 28.474C117.784 29.6393 116.619 30.5593 115.086 31.234C113.583 31.878 111.82 32.2 109.796 32.2Z"
        fill="currentColor"
      />
      <path
        d="M123.454 18.952C123.454 16.376 123.96 14.0913 124.972 12.098C126.015 10.1047 127.41 8.57133 129.158 7.498C130.937 6.42467 132.915 5.888 135.092 5.888C136.993 5.888 138.649 6.27133 140.06 7.038C141.501 7.80467 142.651 8.77067 143.51 9.936V6.302H149.996V31.786H143.51V28.06C142.682 29.256 141.532 30.2527 140.06 31.05C138.619 31.8167 136.947 32.2 135.046 32.2C132.899 32.2 130.937 31.648 129.158 30.544C127.41 29.44 126.015 27.8913 124.972 25.898C123.96 23.874 123.454 21.5587 123.454 18.952ZM143.51 19.044C143.51 17.48 143.203 16.146 142.59 15.042C141.977 13.9073 141.149 13.0487 140.106 12.466C139.063 11.8527 137.944 11.546 136.748 11.546C135.552 11.546 134.448 11.8373 133.436 12.42C132.424 13.0027 131.596 13.8613 130.952 14.996C130.339 16.1 130.032 17.4187 130.032 18.952C130.032 20.4853 130.339 21.8347 130.952 23C131.596 24.1347 132.424 25.0087 133.436 25.622C134.479 26.2353 135.583 26.542 136.748 26.542C137.944 26.542 139.063 26.2507 140.106 25.668C141.149 25.0547 141.977 24.196 142.59 23.092C143.203 21.9573 143.51 20.608 143.51 19.044Z"
        fill="currentColor"
      />
      <path
        d="M154.63 19.044C154.63 16.4067 155.166 14.1067 156.24 12.144C157.313 10.1507 158.8 8.61733 160.702 7.544C162.603 6.44 164.78 5.888 167.234 5.888C170.392 5.888 172.999 6.68533 175.054 8.28C177.139 9.844 178.534 12.052 179.24 14.904H172.294C171.926 13.8 171.297 12.9413 170.408 12.328C169.549 11.684 168.476 11.362 167.188 11.362C165.348 11.362 163.891 12.0367 162.818 13.386C161.744 14.7047 161.208 16.5907 161.208 19.044C161.208 21.4667 161.744 23.3527 162.818 24.702C163.891 26.0207 165.348 26.68 167.188 26.68C169.794 26.68 171.496 25.5147 172.294 23.184H179.24C178.534 25.944 177.139 28.1367 175.054 29.762C172.968 31.3873 170.362 32.2 167.234 32.2C164.78 32.2 162.603 31.6633 160.702 30.59C158.8 29.486 157.313 27.9527 156.24 25.99C155.166 23.9967 154.63 21.6813 154.63 19.044Z"
        fill="currentColor"
      />
      <path
        d="M191.456 11.592V23.92C191.456 24.7787 191.655 25.4073 192.054 25.806C192.483 26.174 193.188 26.358 194.17 26.358H197.16V31.786H193.112C187.684 31.786 184.97 29.1487 184.97 23.874V11.592H181.934V6.302H184.97V0H191.456V6.302H197.16V11.592H191.456Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** The sidebar lockup: the 32px tile beside the wordmark (DESIGN-V2 §6, §11). */
function BrandLockup(): ReactNode {
  return (
    <span className="t-shell-brandlockup">
      <BrandTile size={32} />
      <BrandWordmark height={15} />
    </span>
  );
}

export interface ShellDestination {
  readonly key: string;
  readonly label: string;
  /** Stable navigation target — the anchor's href. Routing stays the surface's. */
  readonly href: string;
  readonly current: boolean;
  /** Optional SPA hook; the anchor still carries href for every other gesture. */
  readonly onSelect?: () => void;
  /** Links that leave the app (Manual) open in a new tab, referrer withheld. */
  readonly newTab?: boolean;
}

function NavLink(props: { readonly item: ShellDestination }): ReactNode {
  const { item } = props;
  return (
    <a
      className={item.current ? "t-shell-navlink t-shell-navlink--current" : "t-shell-navlink"}
      href={item.href}
      aria-current={item.current ? "page" : undefined}
      target={item.newTab === true ? "_blank" : undefined}
      rel={item.newTab === true ? "noreferrer" : undefined}
      onClick={
        item.onSelect === undefined
          ? undefined
          : (e) => {
              if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
              e.preventDefault();
              item.onSelect?.();
            }
      }
    >
      {item.label}
      {/* B282 — a utility link that leaves the app owes the same warning an
          ordinary one does; the wording is the package's, not this file's. */}
      {item.newTab === true ? <NewTabHint /> : null}
    </a>
  );
}

/** B985 — the id a shell's `<main>` carries so its skip link can reach it (one shell per page). */
export const SKIP_TARGET_ID = "transact-main";

/**
 * B985 — WCAG 2.4.1 Bypass Blocks: a shell's FIRST Tab stop, hidden until
 * focused, that hands the focus to the content past the rail and header.
 * The press moves the focus itself and keeps the address: the apps route by
 * path, and a `#fragment` would ride into their popstate. The href stays so
 * the link is a real link (and works with scripting off).
 */
export function SkipLink(props: { readonly target?: string; readonly label?: string }): ReactNode {
  const target = props.target ?? SKIP_TARGET_ID;
  return (
    <a
      className="t-skip-link"
      href={`#${target}`}
      onClick={(e) => {
        const el = e.currentTarget.ownerDocument.getElementById(target);
        if (el === null) return;
        e.preventDefault();
        /* The target takes the focus only when sent there: a static tabindex
           on <main> would also take it from every click on the page's blank
           space (B982 reads such a click as "leave the focus alone"). */
        if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
        el.focus();
      }}
    >
      {props.label ?? "Skip to content"}
    </a>
  );
}

/**
 * The shell: sidebar (brand, destinations, utility) + header (search,
 * actions) + main content. All slots are the surface's to fill.
 */
export function AppShell(props: {
  readonly destinations: readonly ShellDestination[];
  /** Persistent sidebar links off the daily path (e.g. Manual). */
  readonly utility?: readonly ShellDestination[];
  /** Header search / ⌘K entry point (B95 lands here). */
  readonly search?: ReactNode;
  /** Header end: theme toggle, profile, sign out. */
  readonly actions?: ReactNode;
  readonly brand?: ReactNode;
  /**
   * B982 — the address the content draws. A door that opens another page
   * unmounts itself; when the key changes and the focus fell to `<body>`, it
   * lands on the new page's heading (B930's rule). Absent = a static shell.
   */
  readonly viewKey?: string;
  readonly children?: ReactNode;
}): ReactNode {
  const main = useViewFocus<HTMLElement>(props.viewKey ?? "");
  return (
    // B368: the shell's root IS the canvas (B352's rule applied to the one
    // chrome that had skipped it) — glass reads over variation, never a flat
    // solid, and the header can sit bandless on the same sky as the frames.
    <AppCanvas className="t-shell">
      <SkipLink />
      <aside className="t-shell-sidebar">
        <div className="t-shell-brand">{props.brand ?? <BrandLockup />}</div>
        <nav className="t-shell-nav" aria-label="Primary">
          {props.destinations.map((d) => (
            <NavLink key={d.key} item={d} />
          ))}
        </nav>
        {props.utility !== undefined && props.utility.length > 0 && (
          <nav className="t-shell-utility" aria-label="More">
            {props.utility.map((d) => (
              <NavLink key={d.key} item={d} />
            ))}
          </nav>
        )}
      </aside>
      <div className="t-shell-body">
        <header className="t-shell-header">
          <div className="t-shell-search">{props.search}</div>
          <div className="t-shell-actions">{props.actions}</div>
        </header>
        <main ref={main} id={SKIP_TARGET_ID} className="t-shell-content">
          {props.children}
        </main>
      </div>
    </AppCanvas>
  );
}
